import React, { useState, useEffect } from 'react';
import axios from 'src/api/axios';
import { NotificationManager } from 'react-notifications';
import { useCookies } from 'react-cookie';
import { useSelector, useDispatch } from 'react-redux';
import { showModal } from 'src/redux/actions/modalActions';

const UserEmailModal = ({ isOpen, onClose, user }) => {
    const [cookies] = useCookies(['cookieConsent']);
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === 'true';
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorType, setErrorType] = useState(null);

    // Check if user can send email and auto-fill form when modal opens
    useEffect(() => {
        if (isOpen) {
            // Check if user is logged in
            if (!auth.isLoggedIn) {
                setShowError(true);
                setErrorMessage('Please log in to contact the seller by email.');
                setErrorType('not_logged_in');
                return;
            }
            
            // Check if user's email is verified
            if (!auth.user?.has_verified_email) {
                setShowError(true);
                setErrorMessage('Please verify your email address in your account settings to contact the seller by email.');
                setErrorType('email_not_verified');
                return;
            }
            
            // Auto-fill name and email for logged in users with verified email
            if (auth.user) {
                const fullName = auth.user.first_name && auth.user.last_name 
                    ? `${auth.user.first_name} ${auth.user.last_name}`.trim()
                    : auth.user.profile_name || '';
                
                setFormData(prev => ({
                    ...prev,
                    name: fullName || prev.name,
                    email: auth.user.email || prev.email,
                }));
            }
        }
    }, [isOpen, auth.isLoggedIn, auth.user]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: '',
            });
            setErrors({});
            setIsSubmitting(false);
            setShowError(false);
            setErrorMessage('');
            setErrorType(null);
        }
    }, [isOpen]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prevErrors) => {
                const newErrors = { ...prevErrors };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        setIsSubmitting(true);

        try {
            let requestData = { ...formData };

            // Load and execute reCAPTCHA only if enabled
            if (recaptchaEnabled) {
                if (!window.grecaptcha) {
                    await loadRecaptcha();
                }

                const recaptchaToken = await window.grecaptcha.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {
                    action: 'send_email'
                });
                requestData.recaptcha_token = recaptchaToken;
            }

            const response = await axios.post(`/api/user/${user.slug}/send-email`, requestData, { disableNotification: true });

            // Prikaži odgovarajuću poruku u zavisnosti da li je poslato podršci ili direktno
            if (response.data.sent_to_support) {
                NotificationManager.info(response.data.message || 'Your message has been sent to our support team.');
            } else {
                NotificationManager.success(response.data.message || 'Email sent successfully!');
            }
            onClose();
        } catch (error) {
            console.error('Email send error:', error);
            console.error('Error response:', error.response?.data);

            const errorData = error.response?.data || {};
            const errorTypeValue = errorData.error;

            // Handle specific error types
            if (errorTypeValue === 'not_logged_in' || errorTypeValue === 'email_not_verified' || 
                errorTypeValue === 'seller_email_not_verified') {
                setShowError(true);
                setErrorMessage(errorData.message || error.message || 'An error occurred');
                setErrorType(errorTypeValue);
                setIsSubmitting(false);
                return;
            }

            if (!error.response) {
                NotificationManager.error('No server response');
            } else if (error.response?.status === 400 || error.response?.status === 422) {
                const errorData = error.response.data;
                const validationErrors = errorData.error || errorData.errors || {};

                if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
                    const formattedErrors = {};

                    for (const [key, value] of Object.entries(validationErrors)) {
                        if (Array.isArray(value) && value.length > 0) {
                            formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
                        } else if (typeof value === 'string') {
                            formattedErrors[key] = value;
                        }
                    }

                    setErrors(formattedErrors);
                    NotificationManager.error('Please correct the errors in the form');
                } else if (errorData.error) {
                    NotificationManager.error(String(errorData.error));
                } else if (errorData.message) {
                    NotificationManager.error(String(errorData.message));
                } else {
                    NotificationManager.error('Failed to send email. Please try again.');
                }
            } else {
                NotificationManager.error(error.response?.data?.message || error.message || 'An error occurred');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const loadRecaptcha = () => {
        return new Promise((resolve) => {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(resolve);
                return;
            }

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
            script.async = true;
            script.onload = () => {
                window.grecaptcha.ready(resolve);
            };
            document.body.appendChild(script);
        });
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center animate-fadeIn" onClick={handleBackdropClick}>
                <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto animate-blowUp relative max-h-[90vh] overflow-y-auto">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                
                <h2 className="text-xl font-semibold mb-4">Send Email</h2>
                
                {showError ? (
                    <div className="flex flex-col items-center mb-8 bg-red-50 rounded-lg p-6">
                        <div className="bg-red-100 rounded-full p-4 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <p className="text-red-800 text-center font-medium mb-4">{errorMessage}</p>
                        {(errorType === 'not_logged_in' || errorType === 'email_not_verified') && (
                            <div className="flex gap-3 mt-4">
                                {errorType === 'not_logged_in' && (
                                    <>
                                        <button
                                            onClick={() => {
                                                onClose();
                                                dispatch(showModal('LOGIN_MODAL'));
                                            }}
                                            className="bg-[#832EE6] hover:bg-purple-800 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                        >
                                            Log in
                                        </button>
                                        <button
                                            onClick={() => {
                                                onClose();
                                                dispatch(showModal('REGISTER_MODAL'));
                                            }}
                                            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-medium transition-colors"
                                        >
                                            Sign Up
                                        </button>
                                    </>
                                )}
                                {errorType === 'email_not_verified' && (
                                    <button
                                        onClick={() => {
                                            onClose();
                                            window.location.href = '/my/settings';
                                        }}
                                        className="bg-[#832EE6] hover:bg-purple-800 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                                    >
                                        Verify Email
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                ) : !cookies.cookieConsent?.necessary ? (
                    <div className="flex flex-col items-center mb-8 bg-yellow-50 rounded-lg p-6">
                        <div className="bg-yellow-100 rounded-full p-4 mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <p className="text-yellow-800 text-center font-medium">Please accept cookies to send email</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {user && !user.show_email && (
                            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <div>
                                    <p className="text-amber-800 text-sm font-medium">The seller has chosen not to display their email address.</p>
                                    <p className="text-amber-700 text-sm mt-1">Your message will be sent to our support team who will forward it to the seller if appropriate.</p>
                                </div>
                            </div>
                        )}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Your Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`bg-gray-50 border rounded-md focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-2.5 text-sm ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="John Doe"
                                required
                                disabled={isSubmitting}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Your Email <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={`bg-gray-50 border rounded-md focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-2.5 text-sm ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="john@example.com"
                                required
                                disabled={isSubmitting}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                                Subject <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="subject"
                                name="subject"
                                value={formData.subject}
                                onChange={handleChange}
                                className={`bg-gray-50 border rounded-md focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-2.5 text-sm ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Subject"
                                required
                                disabled={isSubmitting}
                            />
                            {errors.subject && (
                                <p className="text-red-500 text-xs mt-1">{errors.subject}</p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                                Message <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                rows={6}
                                className={`bg-gray-50 border rounded-md focus:ring-[#832EE6] focus:border-[#832EE6] block w-full p-2.5 text-sm resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Your message..."
                                required
                                disabled={isSubmitting}
                            />
                            {errors.message && (
                                <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                            )}
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`flex-1 bg-[#832EE6] hover:bg-purple-800 text-white px-4 py-2 rounded-md transition-colors duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </form>
                )}
                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes blowUp {
                    0% {
                        transform: scale(0.8);
                        opacity: 0;
                    }
                    50% {
                        transform: scale(1.05);
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }

                .animate-blowUp {
                    animation: blowUp 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default UserEmailModal;

